/* eslint-disable no-param-reassign */

import { getAttributeBottomUp, adjustStyle } from './ParserUtils';
export type TextFormatDelta = {
    fontfamily: string;
    fontsize: string;
    fontcolor: string;
    highlight: string;
    bold: string;
    italic: string;
    underline: string;
    strikeline: string;
    url: string;
    spc: string;
};

export type ITextFormatRunDelta = { text: string; startIndex: number; endIndex: any; format: TextFormatDelta | null };
export type ITextFormatRunsDelta = ITextFormatRunDelta[];

function fillFontBasic(node: any, getComputedStyle: any, info: { style: any }) {
    // 获取 文字字体
    let fontfamily = adjustStyle(node, 'font-family', getComputedStyle);
    if (fontfamily) {
        const split = fontfamily.indexOf(',');
        // 提前主要的字体
        if (split !== -1) {
            fontfamily = fontfamily.slice(0, split);
        }
        // 去除字体带的双引号
        if (fontfamily[0] === '"') {
            fontfamily = fontfamily.slice(1, fontfamily.length - 1);
        }
        info.style.fontfamily = fontfamily;
    } else {
        info.style.fontfamily = null;
    }
    const styleSize = adjustStyle(node, 'font-size', getComputedStyle);
    if (styleSize) {
        // eslint-disable-next-line radix
        if (styleSize.toString().toLowerCase()
            .indexOf('pt') !== -1) {
            info.style.fontsize = +styleSize.slice(0, -2);
        } else if (styleSize.toString().toLowerCase()
            .indexOf('rem') !== -1) {
            info.style.fontsize = +styleSize.slice(0, -3) * 16;
        }  else if (styleSize.toString().toLowerCase()
            .indexOf('em') !== -1) {
            info.style.fontsize = +styleSize.slice(0, -2) * 16;
        } else {
            info.style.fontsize = parseInt(styleSize, 10);
        }
    }
}

function fillFontColor(node: any, getComputedStyle: any, getHexColor: any, info: { style: any }) {
    let colorStyle;
    if (node.tagName.toLowerCase() === 'a') { // a标签不继承父节点颜色
        colorStyle = node.style.color; // a标签就不向上找了
    } else {
        colorStyle = adjustStyle(node, 'color', getComputedStyle);
    }
    const color = getHexColor(colorStyle);
    if (color) {
        info.style.fontcolor = color;
    }
    // 获取 文字高亮颜色
    const backgroundColorStyle = adjustStyle(node, 'background-color', getComputedStyle);
    const backgroundColor = getHexColor(backgroundColorStyle);
    if (backgroundColor) {
        info.style.highlight = backgroundColor;
    }
}


function fillFontAdv(node: any, getComputedStyle: any, info: { style: any }) {
    const fontWeight = adjustStyle(node, 'font-weight', getComputedStyle);
    if ((fontWeight && (fontWeight === 'bold' || +fontWeight > 400)) || node.tagName === 'B') {
        info.style.bold = 1;
    }
    const lowerCaseTagName = node.tagName.toLowerCase();
    if (lowerCaseTagName === 'strong' || lowerCaseTagName === 'h1' || lowerCaseTagName === 'h2') {
        info.style.bold = 1;
    }
    // 获取 斜体
    const fontStyle = adjustStyle(node, 'font-style', getComputedStyle);
    if (fontStyle === 'italic') {
        info.style.italic = 1;
    }
    if (node.tagName.toLowerCase() === 'i') {
        info.style.italic = 1;
    }
}

function fillFontOthers(node: any, getComputedStyle: any, info: { style: any }) {
    // 超链接样式
    const href = getAttributeBottomUp(node, 'href');
    if (href) {
        info.style.url = href;
    }

    // google docs通过修改css的text-decoration属性来达到添加中划线和下划线的目的，excel通过css添加下划线
    const textDecoration = adjustStyle(node, 'text-decoration', getComputedStyle);
    if (textDecoration) {
        if (textDecoration.indexOf('underline') !== -1) {
            info.style.underline = 'underline';
        }
        if (textDecoration.indexOf('line-through') !== -1) {
            info.style.strikeline = 'strikeline';
        }
    }
    // 文字间距
    const letterSpacing = adjustStyle(node, 'letter-spacing', getComputedStyle);
    if (letterSpacing && letterSpacing !== 'normal' && letterSpacing !== 'inherit') {
        const spc = parseInt(letterSpacing, 10);
        info.style.spc = spc;
    }
    // 行高
    const lineHeight = adjustStyle(node, 'line-height', getComputedStyle);
    if (lineHeight) {
        info.style.lineHeight = lineHeight.includes('%') ? +lineHeight.slice(0, -1) / 100 : lineHeight;
    }
}

/**
 * 只应处理以下以下几种 [NodeType](https://developer.mozilla.org/zh-CN/docs/Web/API/Node/nodeType)
 * > 元素 | 文字 | Document | DocumentFragment
 */
const shouldParseNode = function (node: Node): boolean {
    const list = [Node.ELEMENT_NODE, Node.TEXT_NODE, Node.DOCUMENT_NODE, Node.DOCUMENT_FRAGMENT_NODE];
    return !!node && list.includes(node.nodeType);
};

/**
 *
 * @param node dom节点
 * @param opt
 *          needSpace:处理节点的时候是否保留空格，默认不保留，如果节点带有style mso-spacerun:yes则需要保留
 */
const getFlatNodeStyle = function (node: any, getHexColor: any, getComputedStyle: any, opt: any): any {
    if (!shouldParseNode(node)) return null;

    // 当前节点是个文本节点，没有任何样式属性，直接返回
    if (node.nodeType === Node.TEXT_NODE) {
        let { textContent } = node;
        if (!opt.needSpace) {
            textContent = textContent?.replace(/\s+?/g, ' ');
        }

        return {
            len: textContent?.length,
            format: null,
            text: textContent,
        };
    }

    let needSpace = false;
    try {
        needSpace = node.getAttribute('style') === 'mso-spacerun:yes';
    } catch (error) {
        window.log.info('node.getAttribute(\'style\') === \'mso-spacerun:yes error\'');
    }

    // 开始获取当前节点样式
    const info: { style: any } = { style: {} };

    // 获取 文字基础样式
    fillFontBasic(node, getComputedStyle, info);
    // 获取 文字颜色
    fillFontColor(node, getComputedStyle, getHexColor, info);
    // 获取 字体加粗
    fillFontAdv(node, getComputedStyle, info);
    // 获取 其他属性
    fillFontOthers(node, getComputedStyle, info);

    const { childNodes } = node;
    if (childNodes?.length) {
        let nodes: any[] = [];
        childNodes.forEach((childNode: any) => {
            nodes = nodes.concat(getFlatNodeStyle(childNode, getHexColor, getComputedStyle, { needSpace }));
        });

        for (const nodeItem of nodes) {
            if (nodeItem) {
                if (nodeItem.format) {
                    nodeItem.format = Object.assign({}, info.style, nodeItem.format);
                } else {
                    nodeItem.format = Object.assign({}, info.style);
                }
            }
        }
        return nodes;
    }
    return null;
};

const convertFontHtml2Obj = function (element: any, getHexColor: any, getComputedStyle: any): ITextFormatRunsDelta {
    if (!getComputedStyle) {
        return [];
    }
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const elementText = getElementText(element);
    const textFormatRunsDelta: ITextFormatRunsDelta = [];
    const flatNodes = getFlatNodeStyle(element, getHexColor, getComputedStyle, {});
    let index = 0;

    if (flatNodes) {
        for (const node of flatNodes) {
            if (node) {
                const startIndex = index;
                const endIndex = index + node.len - 1;
                const text = elementText.substring(startIndex, endIndex + 1); // 不包括尾，所以+1
                textFormatRunsDelta.push({
                    text,
                    startIndex,
                    endIndex,
                    format: node.format,
                });
                index += node.len;
            }
        }
    }

    // 复制进来的时候html转textFormatRuns的时候，如果没有富文本样式，会存在一个textFormatRun对象，且此对象的format的值为null
    // 由于textFormatRun的优先级最高，所以会表现为没有样式，针对这种情况，转html的时候增加一层判断
    if (textFormatRunsDelta.length === 1 && !textFormatRunsDelta[0].format) {
        return [];
    }
    return textFormatRunsDelta;
};

// eslint-disable-next-line consistent-return
const getElementText = (element) => {
    if (element) {
        let innerHTML;
        if (element.nodeType === Node.TEXT_NODE) {
            element.innerText = element.textContent;
        } else {
            innerHTML = element.innerHTML;
        }

        if (innerHTML?.indexOf(/<span style="mso-spacerun:yes">/)) {
            element.innerHTML = innerHTML.replace(
                /<span .*style="mso-spacerun:yes">\s*<\/span>/g,
                '',
            );
        }
        let innerText = element.innerText || element.textContent || '';
        // 注意这里 修改了innerText, 富文本对应的解析逻辑也需要进行对应的变动，不然富文本嗯样式对不上位置
        // innerText = innerText.replace(/^\s+|\s+$/g, ''); // 去掉头尾空格或换行
        innerText = innerText?.replace(/\u00a0/g, ' ');
        if (innerText && innerText !== '　' && innerText !== '\xa0') {
            return innerText;
        }
        return '';
    }
};

export { convertFontHtml2Obj };
